import { ShieldCheck, X, Wallet } from 'lucide-react';
import { useState, useEffect } from 'react';

/**
 * Public-facing "Phone Verification" security notice — tells villagers WHY
 * we ask for OTP: to protect their Digital Khata credit from being used by
 * anyone else. High-contrast, text-heavy, dismissable but persistent (comes
 * back on new session).
 *
 * Rendered near the top of Home. Also usable inside AuthModal (compact mode).
 *
 * Props:
 *   compact  : boolean — smaller inline copy for use inside dialogs
 */
export default function SecurityNotice({ compact = false }) {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Reset on load — banner re-appears each fresh session
    const key = 'mandispot_security_notice_dismissed';
    if (sessionStorage.getItem(key) === '1') setDismissed(true);
  }, []);

  if (dismissed) return null;

  const doDismiss = () => {
    sessionStorage.setItem('mandispot_security_notice_dismissed', '1');
    setDismissed(true);
  };

  if (compact) {
    return (
      <div
        data-testid="security-notice-compact"
        className="rounded-lg border-2 border-[#0D3F12] bg-[#E8F5E9] p-3 text-xs text-ink"
      >
        <div className="inline-flex items-center gap-1.5 font-black uppercase tracking-wider text-[#0D3F12] mb-1">
          <ShieldCheck className="w-3.5 h-3.5"/>Why we ask for your phone
        </div>
        <div className="text-ink/85">
          Phone verification protects <b>your Khata (Udhaar)</b> — no one else can use your credit line without an OTP from your phone. This is <b>for your own security</b>.
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid="home-security-notice"
      className="max-w-3xl mx-auto px-4 md:px-6 pt-3"
    >
      <div className="relative rounded-xl border-2 border-[#0D3F12] bg-[#F1F8E9] p-4 flex items-start gap-3">
        <button
          data-testid="security-notice-dismiss"
          onClick={doDismiss}
          className="absolute top-2 right-2 text-ink/40 hover:text-ink"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4"/>
        </button>
        <div className="w-10 h-10 rounded-full bg-[#0D3F12] text-white grid place-items-center shrink-0">
          <ShieldCheck className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#0D3F12]">
            Security notice for our villagers
          </div>
          <div className="font-heading font-black text-base text-ink mt-0.5">
            Phone Verification — for <u>your</u> safety, not ours.
          </div>
          <p className="text-xs md:text-sm text-ink/80 mt-1 leading-snug">
            We ask for your <b>phone number and OTP</b> so <b>no one else can use your Khata (Udhaar) credit</b>. It's the same reason your bank sends you an OTP — only <i>you</i> should be able to buy on your name.
          </p>
          <ul className="mt-2 text-[11px] text-ink/70 grid grid-cols-1 md:grid-cols-3 gap-y-1 md:gap-x-4">
            <li className="inline-flex items-center gap-1"><Wallet className="w-3 h-3 text-[#0D3F12]"/>Protects your credit line</li>
            <li className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#0D3F12]"/>Stops fake orders on your name</li>
            <li className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#0D3F12]"/>Nobody sees your number except your shop</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
